import { createFileRoute, Link } from "@tanstack/react-router";
import { EsPageShell, EsSection } from "@/components/site/EsPageShell";
import { esHead, esWebPageSchema, SITE } from "@/lib/es-pages";

const FAQS: { q: string; a: string }[] = [
  {
    q: "¿Qué IMC exige Medicaid de Virginia para los medicamentos para bajar de peso?",
    a: "Medicaid de Virginia (Cardinal Care) tiene el criterio más estricto de cualquier pagador importante del estado: un IMC mayor de 40, o mayor de 37 con colesterol alto, presión alta o diabetes tipo 2. También debe haber probado y fallado un medicamento para bajar de peso que no sea GLP-1 y el producto preferido del plan, y su proveedor debe certificar que su obesidad es incapacitante y pone en riesgo su vida.",
  },
  {
    q: "¿Cambió la cobertura de los empleados estatales de Virginia?",
    a: "Sí. A partir del 1 de julio de 2026, los planes de empleados estatales de Virginia (COVA) exigen un IMC de 35 o más para cubrir un GLP-1 para bajar de peso, un aumento respecto al estándar anterior de 27/30. Los pacientes actuales no quedan exentos de esta nueva regla: quedan sujetos a ella en cuanto vence su autorización previa.",
  },
  {
    q: "¿Qué se considera un intento fallido con otro medicamento?",
    a: "Cardinal Care lo define con precisión: para medicamentos antiguos como la fentermina, significa tres meses sin perder 10 libras; para el orlistat, seis meses sin perder 10 libras; y para un GLP-1, seis meses sin lograr una reducción del 5 por ciento del peso corporal.",
  },
  {
    q: "¿Por qué cambió el criterio de COVA?",
    a: "El Departamento de Recursos Humanos de Virginia (DHRM) reportó que el gasto en GLP-1 creció de 30 a 200 millones de dólares, con Wegovy como el medicamento de mayor gasto del plan. Elevar el umbral de IMC se adoptó como medida de contención de costos, descrita en los documentos del plan como «similar a Medicaid».",
  },
  {
    q: "Si ya no cumplo con el nuevo umbral de IMC de COVA, ¿tengo otra opción?",
    a: "Sí, revise si aplica la vía alterna por diagnóstico: la apnea del sueño, la diabetes tipo 2, la enfermedad cardiovascular establecida o la enfermedad hepática MASH se rigen por criterios distintos a los de la cobertura para bajar de peso. Si ninguno aplica, puede considerar el pago propio o las opciones quirúrgicas, que la mayoría de los planes de Virginia sí cubren.",
  },
];

export const Route = createFileRoute("/es/seguro/medicaid-y-cardinal-care")({
  head: () => {
    const base = esHead("insuranceMedicaid");
    return {
      ...base,
      scripts: import.meta.env.SSR ? [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              esWebPageSchema("insuranceMedicaid"),
              {
                "@type": "FAQPage",
                "@id": `${SITE}/es/seguro/medicaid-y-cardinal-care#faq`,
                inLanguage: "es-US",
                mainEntity: FAQS.map((f, i) => ({
                  "@type": "Question",
                  "@id": `${SITE}/es/seguro/medicaid-y-cardinal-care#faq-${i + 1}`,
                  name: f.q,
                  acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
              },
            ],
          }),
        },
      ] : [],
    };
  },
  component: EsInsuranceMedicaid,
});

function EsInsuranceMedicaid() {
  return (
    <EsPageShell
      page="insuranceMedicaid"
      title="Medicaid de Virginia y cobertura para empleados estatales"
      intro="Cardinal Care y los planes estatales COVA tienen las reglas más estrictas de Virginia para medicamentos GLP-1, y ambas cambiaron el 1 de julio de 2026."
    >
      <EsSection id="empezar" heading="Antes de empezar">
        <p>
          Si es la primera vez que revisa este tema, conviene leer primero el{" "}
          <Link to="/es/cobertura-de-seguro" className="font-medium text-brand-logo hover:underline">
            resumen general de cobertura de seguro
          </Link>
          , que explica los dos filtros y la vía alterna por diagnóstico antes de entrar en las
          reglas específicas de cada plan.
        </p>
      </EsSection>

      <EsSection id="medicaid" heading="Medicaid de Virginia (Cardinal Care)">
        <p>
          Medicaid de Virginia sí cubre estos medicamentos, pero con el criterio más estricto de
          cualquier pagador importante del estado. Las reglas siguientes provienen del formulario
          oficial de autorización para el manejo del peso de la Mancomunidad, vigente desde el 1 de
          julio de 2026, y aplican tanto a los miembros de pago por servicio como a los planes de
          cuidado administrado, incluidos Anthem HealthKeepers Plus, Aetna Better Health, Molina,
          Sentara Community Plan y UnitedHealthcare Community Plan.
        </p>
        <p>Para Zepbound, Wegovy y Saxenda, usted debe cumplir con:</p>
        <ul className="list-disc space-y-1 pl-6">
          <li>
            IMC mayor de 40, o mayor de 37 con colesterol alto, presión alta o diabetes tipo 2; y
          </li>
          <li>
            Haber probado y fallado un medicamento para bajar de peso que no sea GLP-1, o ser
            intolerante a todos ellos; y
          </li>
          <li>Haber probado y fallado primero el producto preferido del plan; y</li>
          <li>No estar tomando otro GLP-1 al mismo tiempo</li>
        </ul>
        <p>
          Un <strong>«intento fallido»</strong> está definido con precisión. Para medicamentos
          antiguos como la fentermina, significa tres meses sin perder 10 libras. Para el orlistat,
          seis meses sin perder 10 libras. Para un GLP-1, seis meses sin lograr una reducción del 5
          por ciento del peso corporal.
        </p>
        <p>
          <strong>Requisitos adicionales.</strong> Su proveedor debe certificar por escrito que su
          obesidad es «incapacitante y pone en riesgo su vida». Debe estar participando en asesoría
          nutricional y en un programa de actividad física. Su expediente debe incluir un plan para
          bajar de peso y mediciones exactas de estatura y peso de los últimos 60 días.
        </p>
        <p>
          <strong>Edades mínimas.</strong> Wegovy y Saxenda en inyección desde los 12 años; Wegovy
          HD, las pastillas de Wegovy y Zepbound desde los 18 años.
        </p>
        <p>
          <strong>Duración de las aprobaciones.</strong> Las aprobaciones de GLP-1 duran seis meses
          a la vez. La renovación exige al menos un 5 por ciento de pérdida de peso respecto a la
          autorización más reciente, y debe presentarse dentro de los 30 días antes del vencimiento.
          Las renovaciones se detienen una vez que el IMC del miembro cae por debajo de 25.
        </p>
        <p>
          <strong>Los GLP-1 en pastilla tienen un requisito adicional.</strong> Para una pastilla de
          GLP-1, también debe documentar una razón médica por la que no puede usar una inyección, o
          demostrar que fallaron dos productos inyectables.
        </p>
      </EsSection>

      <EsSection id="cova" heading="Planes de empleados estatales de Virginia (COVA Care, COVA HealthAware, COVA HDHP)">
        <p>
          <strong>Esto cambió el 1 de julio de 2026, y el cambio fue significativo.</strong> El plan
          de salud de empleados estatales de Virginia ahora exige un <strong>IMC de 35 o más</strong>{" "}
          para cubrir un GLP-1 para bajar de peso, un aumento respecto al estándar anterior de
          27/30. El Departamento de Recursos Humanos (DHRM) lo dice de forma directa: se requiere un
          IMC de 35 o más para obtener un medicamento GLP-1 para bajar de peso.
        </p>
        <p>
          <strong>Los pacientes actuales no quedan exentos.</strong> Quienes ya usaban el medicamento
          quedan sujetos al nuevo criterio de IMC en cuanto vence su autorización previa vigente.
        </p>
        <p>
          <strong>Por qué ocurrió.</strong> En una presentación de abril de 2026 ante el Comité de
          Finanzas y Asignaciones del Senado, el DHRM reportó que el gasto en GLP-1 creció de 30 a
          200 millones de dólares, con Wegovy como el medicamento de mayor gasto del plan (47
          millones) y Zepbound en tercer lugar (34 millones). Elevar el umbral de IMC se adoptó como
          medida de contención de costos, descrita en los documentos del plan como «similar a
          Medicaid».
        </p>
        <p>
          <strong>Lo que usted pagará.</strong> Los GLP-1 recetados para bajar de peso están en el
          Nivel 3 de COVA Care —un copago de 45 dólares por hasta un suministro de 34 días— y no se
          tratan como medicamentos especializados. Un nuevo deducible de farmacia de 150/300 dólares
          ahora aplica a los niveles 2, 3 y 4. Los miembros de COVA HealthAware y COVA HDHP pagan un
          coseguro del 20 por ciento después de su deducible.
        </p>
        <p>
          <strong>Si ya no cumple con el nuevo umbral de IMC:</strong> la vía alterna por diagnóstico
          es lo más importante aquí. La apnea del sueño, la diabetes tipo 2, la enfermedad
          cardiovascular establecida y la enfermedad hepática MASH se rigen por criterios distintos a
          los de la cobertura para bajar de peso; consulte el{" "}
          <Link to="/es/cobertura-de-seguro" className="font-medium text-brand-logo hover:underline">
            resumen general
          </Link>
          . Si ninguno aplica, considere las opciones quirúrgicas, que la mayoría de los planes de
          Virginia siguen cubriendo, o {" "}
          <Link to="/es/contacto" className="font-medium text-brand-logo hover:underline">
            escríbanos
          </Link>{" "}
          para conversar sobre su caso.
        </p>
      </EsSection>

      <EsSection id="preguntas" heading="Preguntas frecuentes">
        <dl className="space-y-4">
          {FAQS.map((f, i) => (
            <div key={f.q} id={`faq-${i + 1}`}>
              <dt className="font-medium text-brand-deep">{f.q}</dt>
              <dd className="mt-1">{f.a}</dd>
            </div>
          ))}
        </dl>
      </EsSection>

      <EsSection id="mas" heading="Más guías de cobertura">
        <p>
          Consulte también la cobertura de{" "}
          <Link
            to="/es/seguro/plan-comercial-y-marketplace"
            className="font-medium text-brand-logo hover:underline"
          >
            planes comerciales y del Marketplace
          </Link>
          , de{" "}
          <Link
            to="/es/seguro/medicare-tricare-y-fehb"
            className="font-medium text-brand-logo hover:underline"
          >
            Medicare, TRICARE y FEHB
          </Link>
          , o vuelva al{" "}
          <Link to="/es/cobertura-de-seguro" className="font-medium text-brand-logo hover:underline">
            resumen general de cobertura
          </Link>
          .
        </p>
      </EsSection>
    </EsPageShell>
  );
}
